import {AfterViewInit, Directive, ElementRef, EventEmitter, Input, NgZone, OnChanges, OnDestroy, Output, SimpleChanges} from '@angular/core';

@Directive({
    selector: '[DualAxis]',
    standalone: false
})
export class DualAxisDirective implements AfterViewInit, OnChanges, OnDestroy {

  @Input() height: string = null;

  /** Native scroll events, re-emitted outside the Angular zone so a scroll does
   *  not run change detection over every row on every frame. Handlers that
   *  change component state must re-enter the zone themselves. */
  @Output() axisScroll = new EventEmitter<Event>();

  private readonly resizeHandler = () => this.updateHeight();
  private readonly scrollHandler = (event: Event) => {
    this.pinColumns();
    this.axisScroll.emit(event);
  };
  private resizeObserver?: ResizeObserver;
  private mutationObserver?: MutationObserver;
  private lastAppliedHeight: string = '';

  constructor(private host: ElementRef, private zone: NgZone) {
  }

  ngAfterViewInit() {
    this.zone.runOutsideAngular(() => {
      // Defer to the next frame: measuring rect.top during ngAfterViewInit
      // returns 0 until the parent flex layout has resolved child positions.
      requestAnimationFrame(() => {
        this.updateHeight();
        this.pinColumns();
      });
      window.addEventListener('resize', this.resizeHandler);

      const host: HTMLElement = this.host.nativeElement;
      host.addEventListener('scroll', this.scrollHandler, { passive: true });
      // Rows are re-rendered on every page/sort/column toggle; new cells need
      // their pin. Observing the whole subtree also covers hosts that only get
      // their table later (card mode first); pinColumns() is a no-op without one.
      this.mutationObserver = new MutationObserver(() => this.pinColumns());
      this.mutationObserver.observe(host, { childList: true, subtree: true });

      // Observe the parent so we catch layout changes that don't fire
      // window.resize (sidebars collapsing, parent containers resizing).
      // Scoped to the parent instead of document.body to avoid firing on
      // every unrelated layout change and to prevent feedback loops when
      // our own height write would bubble up to a body-sized container.
      if (typeof ResizeObserver !== 'undefined' && host.parentElement) {
        this.resizeObserver = new ResizeObserver(() => {
          this.updateHeight();
          this.pinColumns();
        });
        this.resizeObserver.observe(host.parentElement);
      }
    });
  }

  /** Keeps the pinned columns of a direct child table at the scroller's edges with
   *  an inline translateX instead of position:sticky left/right.
   *
   *  Observed in Safari: with sticky cells, a fast vertical scroll showed the
   *  in-flow columns through the pinned ones until the main thread caught up;
   *  giving the cells their own layer (will-change, #1463) did not help, so do
   *  not retry that. A translated in-flow cell is painted into the scroll-content
   *  tiles ahead of the scroll like any other cell, which is what fixes it. The
   *  likely mechanism is WebKit repainting off-screen sticky-cell layers one
   *  main-thread round-trip after the compositor reveals them.
   *
   *  Trade-off: the pin follows the scroll event, so on a horizontal scroll the
   *  pinned columns trail by at least one frame and visibly swim on a fast fling. */
  private pinColumns() {
    const host: HTMLElement = this.host.nativeElement;
    const table = host.querySelector(':scope > table');
    if (!table) {
      return;
    }
    const left = `translateX(${host.scrollLeft}px)`;
    const right = `translateX(${host.scrollLeft - (host.scrollWidth - host.clientWidth)}px)`;
    const apply = (selector: string, transform: string) => {
      table.querySelectorAll<HTMLElement>(selector).forEach(cell => {
        if (cell.style.transform !== transform) {
          cell.style.transform = transform;
        }
      });
    };
    apply('.sticky-column.left', left);
    apply('.sticky-column.sticky-right', right);
  }

  ngOnChanges(changes: SimpleChanges) {
    if ('height' in changes && !changes['height'].firstChange) {
      this.updateHeight();
    }
  }

  ngOnDestroy() {
    window.removeEventListener('resize', this.resizeHandler);
    this.host.nativeElement.removeEventListener('scroll', this.scrollHandler);
    this.resizeObserver?.disconnect();
    this.mutationObserver?.disconnect();
  }

  private updateHeight() {
    const element: HTMLElement = this.host.nativeElement;

    if (this.height) {
      if (element.style.height !== this.height) {
        element.style.height = this.height;
        this.lastAppliedHeight = this.height;
      }
      return;
    }

    // Measure the element's current position. In our target flex-column
    // layouts rect.top is determined by siblings above and is unaffected
    // by the element's own height, so we can measure without clearing
    // the inline style first and avoid the extra reflow.
    const rect = element.getBoundingClientRect();
    const available = window.innerHeight - rect.top;

    if (available <= 0) {
      return;
    }

    const next = `${Math.floor(available)}px`;
    // Short-circuit no-op writes. If the ResizeObserver fires as a
    // side-effect of our own mutation, the next call computes the same
    // value and exits before writing, terminating any feedback loop.
    if (next === this.lastAppliedHeight && element.style.height === next) {
      return;
    }
    this.lastAppliedHeight = next;
    element.style.height = next;
  }

}
